import React, { ReactNode, useRef } from 'react';
import { Input, Select } from 'formik-antd';
import { FormikProps } from 'formik';
import { isEqual } from 'lodash';
import { Row, Col, Tooltip } from 'antd';
import * as Yup from 'yup';
import validator from 'validator';

import {
  SelectTeammateViewModel,
  AssignmentRateTypeEnum,
  AssignmentTeammateTypeEnum,
} from 'api/backofficeApiClient';
import FormItem from 'components/formik/FormItem';
import { isComponentsEqual } from 'utils/reactUtils';
import { getFormItemName } from 'utils/fieldArrayUtils';
import InputNumber from 'components/formik/InputNumber';
import {
  internalReportingOptions,
  getTeammateValueByTeammateType,
} from 'utils/assignmentUtils';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import { AssignmentInfoFormValues } from 'components/modals/AssignmentDataModal/AssignmentInfo';
import { InternalReportRecruiterTypeEnum } from 'enums';

import styles from './InternalReportSection.module.less';

export const initialInternalReportValidationSchema = {
  userId: Yup.string().nullable(),
  rate: Yup.number()
    .nullable()
    .when('rateType', {
      is: (rateType: AssignmentRateTypeEnum) =>
        rateType === AssignmentRateTypeEnum.Percent,
      then: Yup.number()
        .nullable()
        .min(0, 'Rate should be from 0 to 100')
        .max(100, 'Rate should be from 0 to 100'),
    })
    .when('rateType', {
      is: (rateType: AssignmentRateTypeEnum) =>
        rateType === AssignmentRateTypeEnum.Money,
      then: Yup.number()
        .nullable()
        .min(0, 'Rate should be from 0 to 100000')
        .max(100000, 'Rate should be from 0 to 100000'),
    }),
  externalFullName: Yup.string(),
  rateType: Yup.number().nullable(),
  teammateType: Yup.number().nullable(),
};

export const submitInternalReportValidationSchema = {
  ...initialInternalReportValidationSchema,
  externalFullName: Yup.string().when('teammateType', {
    is: (teammateType: AssignmentTeammateTypeEnum) =>
      teammateType === AssignmentTeammateTypeEnum.ExternalRecruiter,
    then: Yup.string()
      .max(120, `External recruiter name should be 120 characters maximum`)
      .required('Field is required'),
  }),
};

type Props = {
  index: number;
  userId?: string;
  formNamePrefix?: string;
  actionButtons?: ReactNode;
  isTeammatesLoaded: boolean;
  teammates: SelectTeammateViewModel[];
  formik: FormikProps<AssignmentInfoFormValues>;
};

export default React.memo(
  (props: Props) => {
    const {
      index,
      actionButtons = null,
      formNamePrefix,
      teammates,
      isTeammatesLoaded,
      formik,
    } = props;

    const formInternalReports = formik?.values?.internalReports || [];
    const isExternalRecruiterField =
      formInternalReports[index].teammateType ===
      AssignmentTeammateTypeEnum.ExternalRecruiter;
    const isTeammateSelected =
      formInternalReports[index].userId || isExternalRecruiterField;

    const userIdFormItemName = getFormItemName({
      name: 'userId',
      index,
      prefix: formNamePrefix,
    });
    const rateFormItemName = getFormItemName({
      name: 'rate',
      index,
      prefix: formNamePrefix,
    });
    const rateTypeFormItemName = getFormItemName({
      name: 'rateType',
      index,
      prefix: formNamePrefix,
    });
    const externalRecruiterNameFormItemName = getFormItemName({
      name: 'externalFullName',
      index,
      prefix: formNamePrefix,
    });
    const teammateTypeFormItemName = getFormItemName({
      name: 'teammateType',
      index,
      prefix: formNamePrefix,
    });

    const InternalReportingRateType = (
      <Select disabled={!isTeammateSelected} name={rateTypeFormItemName}>
        {internalReportingOptions.map((option) => (
          <Select.Option
            value={option.value}
            key={`internal-reporting-${option.value}`}
          >
            {option.label}
          </Select.Option>
        ))}
      </Select>
    );

    const resetRateField = (value?: string) => {
      // InputNumber doesn't invoke onValueChange in NumberFormat component
      // from react-number-format if setting null / undefined
      if (!value) {
        formik.setFieldValue(rateFormItemName, '', false);
        formik.setFieldTouched(rateFormItemName, false, false);

        formik.setFieldValue(
          rateTypeFormItemName,
          AssignmentRateTypeEnum.Percent,
          false,
        );
        formik.setFieldTouched(rateTypeFormItemName, false, false);
      }
      formik.setFieldValue(externalRecruiterNameFormItemName, undefined, false);
      formik.setFieldTouched(externalRecruiterNameFormItemName, false, false);
    };

    const handleTeammateTypeChange = (value: string) => {
      if (value === InternalReportRecruiterTypeEnum.ExternalRecruiter) {
        return formik.setFieldValue(
          teammateTypeFormItemName,
          AssignmentTeammateTypeEnum.ExternalRecruiter,
          false,
        );
      }

      if (validator.isEmail(value)) {
        return formik.setFieldValue(
          teammateTypeFormItemName,
          AssignmentTeammateTypeEnum.PendingTeammate,
          false,
        );
      }

      return formik.setFieldValue(
        teammateTypeFormItemName,
        AssignmentTeammateTypeEnum.Teammate,
        false,
      );
    };

    const handleSelectChange = (value?: string) => {
      resetRateField(value);
      handleTeammateTypeChange(value || '');
    };

    const externalFieldRef: any = useRef();

    return (
      <div>
        <Row gutter={40}>
          <Col xs={24}>
            <Row gutter={20}>
              <Col xs={16}>
                <FormItem name={userIdFormItemName} label="Internal Reporting">
                  <FormikSelectWithLoading
                    name={userIdFormItemName}
                    allowClear
                    showSearch
                    isLoading={!isTeammatesLoaded}
                    placeholder="Start typing or select a member from the list"
                    optionFilterProp="children"
                    onChange={handleSelectChange}
                    key={`internal-reporting-${index}-${teammates.length}`}
                  >
                    {isTeammatesLoaded && (
                      <>
                        <FormikSelectWithLoading.Option
                          key="externalRecruiter"
                          value={
                            InternalReportRecruiterTypeEnum.ExternalRecruiter
                          }
                          label="External / Non-Registered Recruiter"
                        >
                          External / Non-Registered Recruiter
                        </FormikSelectWithLoading.Option>
                        {teammates.map((recruiter) => (
                          <FormikSelectWithLoading.Option
                            key={recruiter.email}
                            value={
                              getTeammateValueByTeammateType(recruiter)
                                ?.value || ''
                            }
                            label={
                              getTeammateValueByTeammateType(recruiter)?.label
                            }
                          >
                            {getTeammateValueByTeammateType(recruiter)?.label}
                          </FormikSelectWithLoading.Option>
                        ))}
                      </>
                    )}
                  </FormikSelectWithLoading>
                </FormItem>
              </Col>
              <Col xs={8}>
                <FormItem name={rateFormItemName} label="Rate">
                  <InputNumber
                    name={rateFormItemName}
                    hideSpinners
                    placeholder="0.00"
                    precision={2}
                    disabled={!isTeammateSelected}
                    addonAfter={InternalReportingRateType}
                  />
                </FormItem>
              </Col>
            </Row>
            {isExternalRecruiterField && (
              <Row gutter={20}>
                <Col xs={24} ref={externalFieldRef}>
                  <FormItem
                    name={externalRecruiterNameFormItemName}
                    label={
                      <>
                        <span>External recruiter *</span>

                        <Tooltip
                          className={styles.modalTooltipOpener}
                          title="Use this field to add a recruiter who will not be registered on myBasePay."
                          trigger="click"
                          placement="bottomLeft"
                          getPopupContainer={() => externalFieldRef.current}
                        >
                          <span className="icon icon-attention-filled" />
                        </Tooltip>
                      </>
                    }
                  >
                    <Input
                      name={externalRecruiterNameFormItemName}
                      placeholder="Enter full name"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>
              </Row>
            )}
          </Col>

          <Col xs={24}>
            <div className={styles.action}>
              {actionButtons && actionButtons}
            </div>
          </Col>
        </Row>
      </div>
    );
  },
  (prevProps: Props, nextProps: Props) => {
    const prevInternalReportValues =
      prevProps.formik.values.internalReports[prevProps.index];
    const nextInternalReportValues =
      nextProps.formik.values.internalReports[prevProps.index];

    return (
      prevProps.index === nextProps.index &&
      // Disabling of rate field depends on selected teammate
      isEqual(
        prevInternalReportValues.userId,
        nextInternalReportValues.userId,
      ) &&
      isEqual(
        prevInternalReportValues.externalFullName,
        nextInternalReportValues.externalFullName,
      ) &&
      isEqual(
        prevInternalReportValues.teammateType,
        nextInternalReportValues.teammateType,
      ) &&
      isComponentsEqual(prevProps.actionButtons, nextProps.actionButtons) &&
      isEqual(prevProps.isTeammatesLoaded, nextProps.isTeammatesLoaded) &&
      isEqual(prevProps.teammates, nextProps.teammates)
    );
  },
);
